import Link from "next/link";
import type { ReactNode } from "react";
import { products } from "./products";

export function SectionHead({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="ec-section-head">
      <p className="ec-eyebrow">{label}</p>
      <h2>{title}</h2>
      {children && <p className="ec-lead">{children}</p>}
    </div>
  );
}
export function ProductGrid({ slugs }: { slugs?: string[] }) {
  return (
    <div
      className={`ec-product-grid ${slugs?.length === 2 ? "ec-product-grid-pair" : ""}`}
    >
      {products
        .filter((p) => !slugs || slugs.includes(p.slug))
        .map((p, i) => (
          <Link
            className="ec-product-card"
            href={`/products/${p.slug}`}
            key={p.slug}
          >
            <span className="ec-product-index">
              <ProductIcon index={products.indexOf(p)} />
              <span>
                {p.category} / {String(i + 1).padStart(2, "0")}
              </span>
            </span>
            <h3>
              {p.name}
              <span aria-hidden="true">↗</span>
            </h3>
            <p>{p.short}</p>
            <span className="ec-card-link">Explore {p.name.toLowerCase()}</span>
          </Link>
        ))}
    </div>
  );
}
export function ProductIcon({ index }: { index: number }) {
  const paths = [
    "M5 4h22v9H5zM5 19h22v9H5zM9 8h1m4 0h9M9 23h1m4 0h9",
    "M16 3 29 10v13l-13 7L3 23V10zM3 10l13 7 13-7M16 17v13M10 6l13 7",
    "m12 6-9 10 9 10m8-20 9 10-9 10M18 3l-4 26",
    "M12 3h8v8h-8zM3 21h8v8H3zM21 21h8v8h-8zM16 11v5M7 21v-5h18v5",
    "M28 16a12 12 0 1 1-24 0 12 12 0 0 1 24 0ZM4 16h24M16 4c-9 8-9 16 0 24 9-8 9-16 0-24",
    "M3 6h26v21H3zM3 11h26m-20 5 5 4-5 4m9 0h6",
  ];
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[index % paths.length]} />
    </svg>
  );
}
export function Steps({ items }: { items: [string, string][] }) {
  return (
    <ol className="ec-steps">
      {items.map(([title, body], i) => (
        <li key={title}>
          <span className="ec-step-number">
            {String(i + 1).padStart(2, "0")}
          </span>
          <h3>{title}</h3>
          <p>{body}</p>
        </li>
      ))}
    </ol>
  );
}
export function FAQ({ items }: { items: [string, string][] }) {
  return (
    <div className="ec-faq">
      {items.map(([q, a]) => (
        <details key={q}>
          <summary>
            {q}
            <span aria-hidden="true">+</span>
          </summary>
          <p>{a}</p>
        </details>
      ))}
    </div>
  );
}
export function FinalCTA({ business = false }: { business?: boolean }) {
  return (
    <section className="ec-final">
      <div className="ec-container ec-final-inner">
        <div>
          <p className="ec-eyebrow">Your next step</p>
          <h2>
            {business
              ? "Start with the workload. Build a clear plan."
              : "Your next application starts here."}
          </h2>
        </div>
        <div>
          <p>
            {business
              ? "Tell us what you run, how it connects and what your team needs to operate it."
              : "Explore the available configurations and choose the right foundation for your project."}
          </p>
          <Link
            className="ec-button"
            href={business ? "/contact" : "/?signup=1"}
          >
            {business ? "Discuss your requirements" : "Create account"}{" "}
            <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
export function Architecture({ compact = false }: { compact?: boolean }) {
  return (
    <figure
      className={`ec-architecture ${compact ? "ec-architecture-compact" : ""}`}
    >
      <figcaption>
        <span className="ec-eyebrow">Connected foundations</span>
        <span>Example architecture</span>
      </figcaption>
      <div className="ec-architecture-access">
        <span>Console</span>
        <span>Cloud Shell</span>
        <span>Supported API reads</span>
      </div>
      <div className="ec-wire" aria-hidden="true" />
      <div className="ec-control">
        <span className="ec-control-mark" aria-hidden="true">
          ⌘
        </span>
        <strong>Ecocloud control plane</strong>
        <span>Configure · inspect · operate</span>
      </div>
      <div className="ec-wire ec-wire-branch" aria-hidden="true" />
      <div className="ec-architecture-nodes">
        {products.slice(0, 3).map((p, i) => (
          <div key={p.slug}>
            <ProductIcon index={i} />
            <strong>{p.name}</strong>
            <span>{["QEMU / KVM", "OCI / Podman", "HTTP workloads"][i]}</span>
          </div>
        ))}
      </div>
      <div className="ec-foundation">
        <span>Networks & firewall rules</span>
        <span>DNS & certificates</span>
      </div>
      {!compact && (
        <p className="ec-caption">
          Logical relationships between platform components. Available features
          depend on the selected service and environment.
        </p>
      )}
    </figure>
  );
}
